import { Rule } from '../base/mutation-rules';
import { SLIDE_PAGE_DATA_TYPES } from '../../data';
import type { STTLTimeNodeType } from '@tencent/slide-sdk-pb-model';
import type { PresentationDataModel } from '../../model/presentation-data-model';
import type { MaybeError } from '../base/base-mutation';

/**
 * 检查seq是否匹配
 * @param presentation
 * @param pageId
 * @param triggerId
 * @param seqType
 * @returns
 */
function checkSeq(
    presentation: PresentationDataModel,
    pageId: string,
    triggerId: string,
    seqType: STTLTimeNodeType,
): MaybeError {
    const page = presentation.store.models.get(pageId, ...SLIDE_PAGE_DATA_TYPES);
    const seq = page?.timing?.getSeqByTriggerId(triggerId);
    if (seq?.type && seq.type !== seqType) {
        return new Error(
            `[animation]: pageId:${pageId}, triggerId:${triggerId}, memory seq type:${seq.type} not match type:${seqType}`,
        );
    }
}

/**
 * 检查 unitTimeNodeList 是否合法
 * @param presentation
 * @param pageId
 * @param triggerId
 * @param index
 * @param isAdd
 * @returns
 */
function checkUnitTimeNodeList(
    presentation: PresentationDataModel,
    pageId: string,
    triggerId: string,
    index: number,
    isAdd = false,
): MaybeError {
    const page = presentation.store.models.get(pageId, ...SLIDE_PAGE_DATA_TYPES);
    const seq = page?.timing?.getSeqByTriggerId(triggerId);

    // 如果是添加，只有 index 越界才是错误的
    if (isAdd) {
        if (seq?.unitTimeNodeList && seq.unitTimeNodeList.length < index) {
            return new Error(
                `[animation]: add/set index:${index} out of range, current unitTimeNodeList length: ${seq.unitTimeNodeList.length}`,
            );
        }
    } else {
        // 如果是删除，没有 unitTimeNodeList 或者 index 越界都是错误的
        if (!seq?.unitTimeNodeList) {
            return new Error(
                `[animation]: pageId:${pageId}, triggerId:${triggerId} index:${index}, unitTimeNodeList not found`,
            );
        }
        if (seq.unitTimeNodeList.length <= index) {
            return new Error(
                `[animation]: move/remove index:${index} out of range, current unitTimeNodeList length: ${seq.unitTimeNodeList.length}`,
            );
        }
    }
}

function commonCheckAnimationData(
    presentation: PresentationDataModel,
    seqType: STTLTimeNodeType,
    shapeId: string,
    pageId: string,
): MaybeError {
    // TODO @ruiqitan 需要检查 triggerId 对应的 Shape 是否存在
    return Rule.shape.should.existInPage(presentation, shapeId, pageId);
}

export function checkAddAnimationData(
    presentation: PresentationDataModel,
    seqType: STTLTimeNodeType,
    triggerId: string,
    shapeId: string,
    pageId: string,
    index: number,
): MaybeError {
    const error = commonCheckAnimationData(presentation, seqType, shapeId, pageId);
    if (error) return error;

    return checkUnitTimeNodeList(presentation, pageId, triggerId, index, true);
}

export function checkSetAnimationData(
    presentation: PresentationDataModel,
    seqType: STTLTimeNodeType,
    triggerId: string,
    shapeId: string,
    pageId: string,
    index: number,
): MaybeError {
    const error = commonCheckAnimationData(presentation, seqType, shapeId, pageId);
    if (error) return error;

    return checkUnitTimeNodeList(presentation, pageId, triggerId, index, false);
}

export function checkRemoveAnimationData(
    presentation: PresentationDataModel,
    seqType: STTLTimeNodeType,
    triggerId: string,
    shapeId: string,
    pageId: string,
    index: number,
): MaybeError {
    const error = checkSeq(presentation, pageId, triggerId, seqType);
    if (error) return error;

    return checkUnitTimeNodeList(presentation, pageId, triggerId, index, false);
}

export function checkMoveAnimationData(
    presentation: PresentationDataModel,
    fromSeqType: STTLTimeNodeType,
    seqType: STTLTimeNodeType,
    fromTriggerId: string,
    triggerId: string,
    shapeId: string,
    pageId: string,
    fromIndex: number,
    index: number,
): MaybeError {
    const error = checkRemoveAnimationData(presentation, fromSeqType, fromTriggerId, shapeId, pageId, fromIndex);
    if (error) return error;

    return checkAddAnimationData(presentation, seqType, triggerId, shapeId, pageId, index);
}
